import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { differenceInDays } from 'date-fns';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import './DaysCalendar.css';

const DaysCalendar = () => {
  const [daysTogether, setDaysTogether] = useState(0);
  const [relationshipStart, setRelationshipStart] = useState(null);

  // Listen for relationship start date updates in real-time
  useEffect(() => {
    const configRef = ref(database, 'config/relationshipStartDate');

    const unsubscribe = onValue(configRef, (snapshot) => {
      if (snapshot.exists()) {
        const startDate = new Date(snapshot.val());
        setRelationshipStart(startDate);
        calculateDays(startDate);
      } else {
        // Default to a year ago if not set
        const defaultDate = new Date();
        defaultDate.setFullYear(defaultDate.getFullYear() - 1);
        setRelationshipStart(defaultDate);
        calculateDays(defaultDate);
      }
    });

    return () => unsubscribe();
  }, []);

  const calculateDays = (startDate) => {
    const today = new Date();
    const days = differenceInDays(today, startDate);
    setDaysTogether(days);
  };

  // Update counter every hour
  useEffect(() => {
    if (!relationshipStart) return;

    const interval = setInterval(() => {
      calculateDays(relationshipStart);
    }, 1000 * 60 * 60);

    return () => clearInterval(interval);
  }, [relationshipStart]);

  // Create a row of hearts based on days (show up to 10 hearts, then add more)
  const renderHearts = () => {
    const heartCount = Math.min(Math.floor(daysTogether / 10), 15);
    const hearts = [];

    for (let i = 0; i < heartCount; i++) {
      hearts.push(
        <motion.span
          key={i}
          className="heart-icon"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.05, duration: 0.3 }}
        >
          ♥
        </motion.span>
      );
    }
    return hearts;
  };

  return (
    <motion.div
      className="days-calendar"
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.3 }}
    >
      <div className="love-counter">
        <motion.div
          className="calendar-graphic"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Calendar SVG */}
          <svg viewBox="0 0 140 160" className="calendar-svg">
            {/* Calendar binding rings */}
            <circle cx="35" cy="12" r="6" fill="#6b5644" />
            <circle cx="35" cy="12" r="3" fill="#f4efe7" />
            <circle cx="70" cy="12" r="6" fill="#6b5644" />
            <circle cx="70" cy="12" r="3" fill="#f4efe7" />
            <circle cx="105" cy="12" r="6" fill="#6b5644" />
            <circle cx="105" cy="12" r="3" fill="#f4efe7" />

            {/* Calendar body */}
            <rect
              x="15"
              y="20"
              width="110"
              height="130"
              rx="4"
              fill="#fef9f3"
              stroke="#a68a64"
              strokeWidth="2"
            />

            {/* Calendar header */}
            <rect
              x="15"
              y="20"
              width="110"
              height="30"
              rx="4"
              fill="#d9989f"
              stroke="#a68a64"
              strokeWidth="2"
            />

            {/* Decorative hearts in header */}
            <text x="40" y="42" fontSize="16" fill="#f2d4d7">♥</text>
            <text x="95" y="42" fontSize="16" fill="#f2d4d7">♥</text>

            {/* Calendar grid lines (horizontal) */}
            <line x1="20" y1="70" x2="120" y2="70" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="20" y1="90" x2="120" y2="90" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="20" y1="110" x2="120" y2="110" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="20" y1="130" x2="120" y2="130" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />

            {/* Calendar grid lines (vertical) */}
            <line x1="35" y1="55" x2="35" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="50" y1="55" x2="50" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="65" y1="55" x2="65" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="80" y1="55" x2="80" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="95" y1="55" x2="95" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />
            <line x1="110" y1="55" x2="110" y2="145" stroke="#d9c9a5" strokeWidth="1" opacity="0.4" />

            {/* Random decorative heart marks on calendar */}
            <text x="25" y="65" fontSize="8" fill="#f2d4d7" opacity="0.6">♥</text>
            <text x="55" y="85" fontSize="8" fill="#f2d4d7" opacity="0.6">♥</text>
            <text x="85" y="105" fontSize="8" fill="#f2d4d7" opacity="0.6">♥</text>
            <text x="40" y="125" fontSize="8" fill="#f2d4d7" opacity="0.6">♥</text>
            <text x="100" y="140" fontSize="8" fill="#f2d4d7" opacity="0.6">♥</text>
          </svg>
        </motion.div>

        <motion.div
          className="days-display"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="days-number">{daysTogether}</div>
          <div className="days-text">days together</div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DaysCalendar;
